// Welle H6: Mikrofon anfragen und Fehler verständlich erklären — EIN Weg für alle Diktate.
// • mikrofonAnfragen(): wie getUserMedia({ audio: true }), aber das Promise hat sofort
//   einen leeren Fehler-Handler. Vorab geholte Streams (iOS: Anfrage muss im Tipp-Kontext
//   passieren) erzeugen so keine „unbehandelte Ablehnung“, wenn niemand sie abwartet —
//   wer sie später awaitet, bekommt den Fehler trotzdem.
// • zeigeMikrofonFehler(err): öffnet den Hinweis (MikrofonHinweisHost) mit Anleitung
//   je Gerät und gibt den kurzen Titel zurück (für bestehende Fehlerzeilen).

export type MikrofonFehlerArt = 'verweigert' | 'nicht_gefunden' | 'belegt' | 'nicht_verfuegbar' | 'unbekannt';
export type Geraet = 'ios' | 'android' | 'pc' | 'unbekannt';

export const MIKROFON_TITEL: Record<MikrofonFehlerArt, string> = {
  verweigert: 'Mikrofon nicht freigegeben.',
  nicht_gefunden: 'Kein Mikrofon gefunden.',
  belegt: 'Das Mikrofon wird gerade von einer anderen App benutzt.',
  nicht_verfuegbar: 'Mikrofon ist in diesem Browser nicht verfügbar.',
  unbekannt: 'Das Mikrofon konnte nicht gestartet werden.',
};

export const MIKROFON_ANLEITUNG: Record<Exclude<Geraet, 'unbekannt'>, string> = {
  ios: 'iPhone/iPad (Safari): Einstellungen › Apps › Safari › Mikrofon › Erlauben, dann die Seite neu laden.',
  android: 'Android (Chrome): Auf das Schloss neben der Adresse tippen › Berechtigungen › Mikrofon.',
  pc: 'PC: Auf das Schloss in der Adresszeile klicken › Mikrofon zulassen.',
};

export function mikrofonFehlerArt(err: unknown): MikrofonFehlerArt {
  const name = (err as { name?: string })?.name ?? '';
  if (['NotAllowedError', 'PermissionDeniedError', 'SecurityError'].includes(name)) return 'verweigert';
  if (['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError'].includes(name)) return 'nicht_gefunden';
  if (['NotReadableError', 'TrackStartError', 'AbortError'].includes(name)) return 'belegt';
  if (name === 'NichtVerfuegbar') return 'nicht_verfuegbar';
  return 'unbekannt';
}

export function erkenneGeraet(ua: string = navigator.userAgent, beruehrung = navigator.maxTouchPoints ?? 0): Geraet {
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && beruehrung > 1)) return 'ios'; // iPadOS meldet sich als Mac
  if (/Android/i.test(ua)) return 'android';
  if (/Windows|Macintosh|Linux|CrOS/i.test(ua)) return 'pc';
  return 'unbekannt';
}

/** Anleitungszeilen für das Gerät; im Zweifel alle drei */
export function mikrofonAnleitung(geraet: Geraet = erkenneGeraet()): string[] {
  return geraet === 'unbekannt' ? Object.values(MIKROFON_ANLEITUNG) : [MIKROFON_ANLEITUNG[geraet]];
}

export function mikrofonAnfragen(): Promise<MediaStream> {
  const p: Promise<MediaStream> = navigator.mediaDevices?.getUserMedia
    ? navigator.mediaDevices.getUserMedia({ audio: true })
    : Promise.reject(Object.assign(new Error('getUserMedia fehlt'), { name: 'NichtVerfuegbar' }));
  p.catch(() => { /* wird beim Abwarten behandelt — hier nur gegen „unbehandelt“ */ });
  return p;
}

export type MikrofonHinweis = {
  art: MikrofonFehlerArt;
  titel: string;
  anleitung: string[];
  /** Knopf „Stattdessen tippen“ — nur wo der Dialog eine Texteingabe hat */
  stattdessenTippen?: () => void;
};

let _zeige: ((h: MikrofonHinweis | null) => void) | null = null;
export function registerMikrofonHinweis(fn: ((h: MikrofonHinweis | null) => void) | null) { _zeige = fn; }

/** Zeigt den Hinweis zum Mikrofon-Fehler und gibt den kurzen Titel zurück. */
export function zeigeMikrofonFehler(err: unknown, opts: { stattdessenTippen?: () => void } = {}): string {
  const art = mikrofonFehlerArt(err);
  const titel = MIKROFON_TITEL[art];
  _zeige?.({ art, titel, anleitung: art === 'verweigert' ? mikrofonAnleitung() : [], stattdessenTippen: opts.stattdessenTippen });
  return titel;
}

/** Fokus auf ein Eingabefeld (nach dem Schließen des Hinweises) */
export function fokussiere(selektor: string) {
  setTimeout(() => (document.querySelector(selektor) as HTMLElement | null)?.focus(), 50);
}
